/**
 * Variant Table Component
 * Interactive variant selector displayed as a table with custom radio buttons
 * Integrates with the existing pubsub system to trigger variant changes
 */

if (!customElements.get('variant-table')) {
  class VariantTable extends HTMLElement {
    constructor() {
      super();
    }

    connectedCallback() {
      // Listen for change events on radio inputs within this table
      this.addEventListener('change', this.handleVariantSelection.bind(this));

      // Set up row click handling for better UX
      this.setupRowClickHandlers();

      // Set initial checked state based on selected variant
      this.initializeSelectedVariant();
    }

    /**
     * Initialize the selected variant based on product JSON
     */
    initializeSelectedVariant() {
      const selectedVariantScript = this.querySelector('[data-selected-variant]');
      if (!selectedVariantScript) return;

      try {
        const selectedVariant = JSON.parse(selectedVariantScript.textContent);
        if (!selectedVariant?.id) return;

        const radio = this.querySelector(`input[value="${selectedVariant.id}"]`);
        if (radio && !radio.checked) {
          radio.checked = true;
        }
      } catch (error) {
        console.error('Error parsing selected variant:', error);
      }
    }

    /**
     * Set up click handlers for table rows to toggle radio buttons
     */
    setupRowClickHandlers() {
      const rows = this.querySelectorAll('.variants--table-row');

      rows.forEach(row => {
        row.addEventListener('click', (event) => {
          // Don't trigger if clicking the radio input itself
          if (event.target.type === 'radio') return;

          const radio = row.querySelector('input[type="radio"]');
          if (radio && !radio.disabled) {
            radio.checked = true;
            // Manually trigger change event since programmatic changes don't fire it
            radio.dispatchEvent(new Event('change', { bubbles: true }));
          }
        });
      });
    }

    /**
     * Handle variant selection when a radio button changes
     * Directly updates the product form and URL with the selected variant
     */
    handleVariantSelection(event) {
      const target = event.target;
      if (target.type !== 'radio') return;

      // Ensure the target has an ID for focus management
      if (!target.id) {
        target.id = `variant-${target.value}-${this.dataset.section}`;
      }

      // Get the variant ID from the radio button value
      const variantId = target.value;
      const variantRow = target.closest('.variants--table-row');

      // Find the parent product-info element
      const productInfo = this.closest('product-info');
      if (!productInfo) {
        console.error('variant-table: Could not find parent product-info element');
        return;
      }

      // Get the selected variant from product JSON
      const variant = this.getVariantById(variantId);
      if (!variant) {
        console.error('variant-table: Could not find variant with ID', variantId);
        return;
      }

      // Update the variant input in the product form
      this.updateProductFormVariant(productInfo, variantId);

      // Update the URL with the variant parameter
      this.updateURLWithVariant(productInfo, variantId);

      // Update visual feedback
      this.updateSelectedState(variantRow);

      // Publish variant change event so other components can react
      publish(PUB_SUB_EVENTS.variantChange, {
        data: {
          sectionId: this.dataset.section,
          variant: variant,
        },
      });
    }

    /**
     * Get variant data by ID from the product JSON
     */
    getVariantById(variantId) {
      const productJson = this.querySelector('[data-product-json]');
      if (!productJson) return null;

      try {
        const product = JSON.parse(productJson.textContent);
        return product.variants?.find(v => v.id.toString() === variantId.toString());
      } catch (error) {
        console.error('Error parsing product JSON:', error);
        return null;
      }
    }

    /**
     * Update the product form with the selected variant ID
     */
    updateProductFormVariant(productInfo, variantId) {
      const sectionId = this.dataset.section;
      const forms = productInfo.querySelectorAll(
        `#product-form-${sectionId}, #product-form-installment-${sectionId}`
      );

      forms.forEach((form) => {
        const input = form.querySelector('input[name="id"]');
        if (input) {
          input.value = variantId;
          input.dispatchEvent(new Event('change', { bubbles: true }));
        }
      });
    }

    /**
     * Update the browser URL with the variant parameter
     */
    updateURLWithVariant(productInfo, variantId) {
      const productUrl = productInfo.dataset.url;
      if (!productUrl) return;

      // Update share button if present
      const shareButton = productInfo.querySelector('share-button');
      if (shareButton && typeof shareButton.updateUrl === 'function') {
        shareButton.updateUrl(`${window.shopUrl}${productUrl}?variant=${variantId}`);
      }

      // Update browser URL if allowed
      if (productInfo.dataset.updateUrl !== 'false') {
        window.history.replaceState({}, '', `${productUrl}?variant=${variantId}`);
      }
    }

    /**
     * Update visual state to show which variant is selected
     */
    updateSelectedState(selectedRow) {
      // Remove any previous selected state classes
      const allRows = this.querySelectorAll('.variants--table-row');
      allRows.forEach(row => {
        row.classList.remove('variants--table-row--selected');
      });

      // Add selected state to the new row
      if (selectedRow) {
        selectedRow.classList.add('variants--table-row--selected');
      }
    }

    /**
     * Get the currently selected variant ID
     */
    getSelectedVariantId() {
      const checkedRadio = this.querySelector('input[type="radio"]:checked');
      return checkedRadio ? checkedRadio.value : null;
    }

    /**
     * Programmatically select a variant by ID
     * @param {string|number} variantId - The variant ID to select
     */
    selectVariant(variantId) {
      const radio = this.querySelector(`input[value="${variantId}"]`);
      if (radio && !radio.disabled) {
        radio.checked = true;
        radio.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }
  }

  customElements.define('variant-table', VariantTable);
}